import { motion, useInView } from "motion/react";
import { useEffect, useId, useRef, useState } from "react";
import { ContactCTA } from "../ContactCTA";
import { PROCESS, PROCESS_STATEMENT, USP_TEXT } from "../../data/agencyData";
import { BRAND } from "../../lib/brand";

/** Seconds for a step to fade in, and for a connector to sweep across. */
const STEP_IN = 0.28;
const ARC_DRAW = 0.42;

/** A step is the last in its row when no connector leaves it. */
const hasArcAfter = (i: number) => i !== 3 && i !== PROCESS.length - 1;

/**
 * Walks the steps in order, so each connector starts once its step has landed
 * and the next step waits for the line to arrive. Returns the delay for every
 * step, every arc, and the closing lasso.
 */
function buildTimeline() {
  const step: number[] = [];
  const arc: number[] = [];
  let t = 0;
  for (let i = 0; i < PROCESS.length; i++) {
    step[i] = t;
    t += STEP_IN;
    if (hasArcAfter(i)) {
      arc[i] = t;
      t += ARC_DRAW;
    }
  }
  return { step, arc, lasso: t };
}

const TIMELINE = buildTimeline();

/** The arcs only exist from `lg` up, so this choreography does too. Everything
 *  below `lg` is one column with the vertical rail in StepNode instead — there
 *  is deliberately no third, connector-less layout in between. */
function useIsLarge() {
  const [isLarge, setIsLarge] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const update = () => setIsLarge(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);
  return isLarge;
}

/**
 * Dashed arc linking one step to the next, as drawn in
 * build/OURPROCESSSection.svg. Figma exported those dashes as outlined fill
 * shapes pinned to 1920px coordinates — redrawn here as a real dashed stroke so
 * it scales with the grid and stays crisp at any width.
 *
 * The curve stretches with the column (preserveAspectRatio="none") while
 * non-scaling-stroke holds the dash weight steady; the arrowhead is a separate
 * fixed-size element so it never gets squashed with it.
 */
function StepArc({ play, delay }: { play: boolean; delay: number }) {
  // Unique per instance so the clip paths never collide.
  const clipId = `gc-arc-${useId().replace(/:/g, "")}`;

  return (
    <span
      className="pointer-events-none absolute left-[22%] -top-[10px] hidden h-[66px] w-[106%] lg:block"
      aria-hidden="true"
    >
      <svg className="block h-full w-full" viewBox="0 0 100 60" preserveAspectRatio="none" fill="none">
        <defs>
          {/*
            The line is "drawn" by sweeping a clip rect across it, rather than by
            animating pathLength — framer implements pathLength through
            strokeDasharray/Dashoffset, which would wipe out the dashed pattern
            that makes these lines dotted in the first place.
          */}
          <clipPath id={clipId}>
            <motion.rect
              x="0"
              y="-20"
              height="100"
              initial={{ width: 0 }}
              animate={play ? { width: 100 } : { width: 0 }}
              // The delay is for arriving only — on the way out everything
              // clears together, or leaving the section becomes a slow
              // four-second unravelling in reverse.
              transition={play ? { duration: ARC_DRAW, delay, ease: "easeInOut" } : { duration: 0.2 }}
            />
          </clipPath>
        </defs>
        {/* Cubic, not quadratic: a quadratic peaks only halfway to its control
            point, giving a flat arc, and pulling the control above the viewBox
            to compensate just gets clipped. Controls at -12 arch the curve to
            y≈5 — high, without leaving the box. */}
        <path
          d="M1 58 C24 -12, 76 -12, 99 52"
          stroke={BRAND.greenBright}
          strokeWidth="2"
          strokeDasharray="6 6"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
          clipPath={`url(#${clipId})`}
        />
      </svg>
      <motion.svg
        className="absolute -right-[4px] bottom-[4px] h-[12px] w-[12px] rotate-[42deg]"
        viewBox="0 0 12 12"
        fill="none"
        initial={{ opacity: 0, scale: 0.5 }}
        animate={play ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.5 }}
        // Lands just as the line reaches the end of its sweep.
        transition={play ? { duration: 0.18, delay: delay + ARC_DRAW * 0.82, ease: "easeOut" } : { duration: 0.15 }}
      >
        <path
          d="M2 1L10 6L2 11"
          stroke={BRAND.greenBright}
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </motion.svg>
    </span>
  );
}

/**
 * Step 07's lasso, matching the design: not concentric rings but one continuous
 * hand-drawn stroke that loops round roughly 1.6 times, crosses over itself on
 * the right, then cuts back through the middle to a left-pointing arrow.
 *
 * The control points are deliberately uneven — a true ellipse reads as a
 * computer shape, and the emphasis here should look pen-drawn. Kept at its own
 * aspect ratio (no preserveAspectRatio="none") so that irregularity is never
 * stretched into looking like a mistake.
 */
const LASSO_PATH = [
  // First pass round, clockwise from the left
  "M14 60",
  "C11 30 58 9 106 11",
  "C150 13 190 27 191 51",
  "C192 74 152 93 105 95",
  "C60 97 18 84 14 60",
  // Second pass, tucked inside the first
  "C11 38 55 22 99 24",
  "C141 26 175 37 174 54",
  "C173 68 152 78 124 74",
  // …then cut back through the middle
  "C104 71 88 66 72 62",
].join(" ");

function IterateLoop({ play, delay }: { play: boolean; delay: number }) {
  return (
    // Entrance animates opacity only: the inner wrapper's wobble is a CSS
    // animation on `transform`, and a motion transform here would fight it.
    <motion.span
      className="pointer-events-none absolute -left-[24%] -top-[26px] hidden h-[150px] w-[132%] lg:block"
      aria-hidden="true"
      initial={{ opacity: 0 }}
      animate={play ? { opacity: 1 } : { opacity: 0 }}
      transition={play ? { duration: 0.45, delay, ease: "easeOut" } : { duration: 0.2 }}
    >
      <span className="gc-lasso-wrap block h-full w-full">
        <svg className="block h-full w-full overflow-visible" viewBox="0 0 200 100" fill="none">
          <path
            className="gc-lasso"
            d={LASSO_PATH}
            stroke={BRAND.greenBright}
            strokeWidth="2"
            strokeDasharray="7 7"
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
          />
          {/* Arrowhead, pointing back the way the stroke is travelling */}
          <path
            d="M77 57.5L71 62L77 66.5"
            stroke={BRAND.greenBright}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />
        </svg>
      </span>
    </motion.span>
  );
}

/**
 * The single-column stand-in for the arcs.
 *
 * The dashed arcs and the lasso are `lg:block` only, because a wide curve
 * between two columns has nowhere to go once the grid is one column across. So
 * below `lg` the connector turns vertical: a numbered node per step with a
 * dashed rail running down to the next one, drawn in as that step arrives — the
 * same "each step waits for the line to reach it" choreography, rotated 90°.
 *
 * Each step watches itself rather than reading a shared timeline: on a phone the
 * seven steps are far taller than the viewport, so a single section-wide cascade
 * would finish long before the reader had scrolled to step 04.
 */
function StepNode({ n, isLast }: { n: string; isLast: boolean }) {
  const ref = useRef<HTMLSpanElement>(null);
  const seen = useInView(ref, { once: true, margin: "-30% 0px -30% 0px" });

  return (
    <span ref={ref} className="pointer-events-none absolute left-0 top-0 bottom-0 w-[40px] lg:hidden" aria-hidden="true">
      {/* The node itself — the ghost numeral from the desktop grid, shrunk into
          a badge so it can sit on the rail instead of floating above the title. */}
      <motion.span
        className="absolute left-0 top-[2px] grid h-[40px] w-[40px] place-items-center rounded-full border bg-[#e7f1f1] font-black text-[15px] leading-none"
        style={{ color: BRAND.navy, borderColor: `${BRAND.navy}2e` }}
        initial={{ opacity: 0, scale: 0.6 }}
        animate={seen ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.6 }}
        transition={{ duration: 0.35, ease: "easeOut" }}
      >
        {n}
      </motion.span>

      {/* Step 07 closes the loop rather than pointing on, so it gets a turning
          dashed ring — the vertical equivalent of the lasso. */}
      {isLast && (
        <motion.span
          className="gc-node-ring absolute left-[-5px] top-[-3px] h-[50px] w-[50px]"
          initial={{ opacity: 0 }}
          animate={seen ? { opacity: 1 } : { opacity: 0 }}
          transition={{ duration: 0.4, delay: 0.25, ease: "easeOut" }}
        >
          <svg viewBox="0 0 50 50" fill="none" className="block h-full w-full">
            <circle
              cx="25"
              cy="25"
              r="24"
              stroke={BRAND.greenBright}
              strokeWidth="2"
              strokeDasharray="6 7"
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
            />
          </svg>
        </motion.span>
      )}

      {/* The rail down to the next node. Scales from the node outward, so it
          reads as being drawn rather than fading in. */}
      {!isLast && (
        <motion.span
          // Overshoots the node at both ends by 4px, so a dash always lands
          // against the badge instead of the pattern's transparent half.
          className="gc-rail absolute left-[19px] top-[46px] bottom-[-46px] w-[2px] origin-top"
          style={{ "--gc-rail-color": BRAND.greenBright } as React.CSSProperties}
          initial={{ scaleY: 0 }}
          animate={seen ? { scaleY: 1 } : { scaleY: 0 }}
          transition={{ duration: 0.45, delay: 0.2, ease: "easeInOut" }}
        />
      )}
    </span>
  );
}

export function ProcessSection() {
  const ref = useRef<HTMLElement>(null);
  // Not `once` — the whole point of this section is the cascade, so it replays
  // each time the section comes back into view.
  const inView = useInView(ref, { margin: "-80px" });
  const isLarge = useIsLarge();

  return (
    <section ref={ref} className="relative w-full py-16 md:py-24 px-4 md:px-[116px] flex flex-col items-center gap-12">
      <div className="w-full flex flex-col gap-6">
        <p className="font-mono text-[12px] md:text-[14px] tracking-[3px] uppercase text-[#666]">How we work</p>
        <h2 className="font-black text-[48px] md:text-[96px] leading-[1.05] tracking-[-2.4px] text-[#141414]">
          Our Process
        </h2>
      </div>

      <ol className="w-full grid gap-x-8 gap-y-10 lg:gap-y-14 grid-cols-1 lg:grid-cols-4">
        {PROCESS.map((step, i) => {
          // Arcs join steps within a row; step 07 gets the lasso instead,
          // since iterating is a cycle rather than a hop to somewhere else.
          const isIterate = i === PROCESS.length - 1;
          // Without connectors on small screens, fall back to a simple stagger —
          // waiting on lines nobody can see would just look like a stall.
          const stepDelay = isLarge ? TIMELINE.step[i] : i * 0.08;

          return (
            <motion.li
              key={step.n}
              initial={{ opacity: 0, y: 24 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }}
              transition={inView ? { duration: STEP_IN + 0.18, delay: stepDelay, ease: "easeOut" } : { duration: 0.2 }}
              // The left gutter below `lg` is the rail's; at `lg` the grid
              // splits into four columns and the ghost numeral takes over again.
              className="relative pl-[56px] lg:pl-0 lg:pt-4"
            >
              {hasArcAfter(i) && <StepArc play={inView && isLarge} delay={TIMELINE.arc[i]} />}
              {isIterate && <IterateLoop play={inView && isLarge} delay={TIMELINE.lasso} />}
              <StepNode n={step.n} isLast={isIterate} />

              {/* Ghost numeral. Kept in flow rather than absolutely positioned:
                  overlapping the title was making both hard to read. */}
              <span
                aria-hidden="true"
                className="hidden lg:block font-black text-[64px] md:text-[86px] leading-[0.8] text-white/70 select-none pointer-events-none"
              >
                {step.n}
              </span>
              <h3 className="relative mt-1.5 lg:mt-4 flex flex-wrap items-center gap-x-3 gap-y-1 font-bold text-[20px] md:text-[22px] leading-tight text-[#141414]">
                <span className="sr-only">Step {step.n}: </span>
                {step.title}
                {/* Solid fill: step 07's chip sits over the lasso, and a
                    transparent one is unreadable against the dashes. */}
                {step.tag && (
                  <span
                    className="rounded-full border bg-[#e7f1f1] px-2.5 py-[3px] font-mono text-[10px] font-normal tracking-[1px] uppercase"
                    style={{ color: BRAND.navy, borderColor: `${BRAND.navy}33` }}
                  >
                    {step.tag}
                  </span>
                )}
              </h3>
              <p className="relative mt-2 text-[14px] md:text-[15px] leading-[1.6] text-[#666] lg:max-w-[300px]">
                {step.body}
              </p>
            </motion.li>
          );
        })}
      </ol>

      {/* Decorative + marks, flanking the statement as in the design */}
      <span aria-hidden="true" className="hidden lg:block absolute left-[9%] top-[63%] text-[#141414] text-xl select-none">
        +
      </span>
      <span aria-hidden="true" className="hidden lg:block absolute left-[9%] top-[78%] text-[#141414] text-xl select-none">
        +
      </span>

      {/* Closing statement — the design's own words */}
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }}
        transition={inView ? { duration: 0.6, delay: 0.3, ease: "easeOut" } : { duration: 0.2 }}
        // Wide measure so it reads across the page, but capped well short of the
        // full width — long lines of 40px type get tiring to track.
        className="font-bold text-[26px] md:text-[40px] leading-[1.3] tracking-tight text-center max-w-[1040px] mt-4"
      >
        {PROCESS_STATEMENT.map((part, i) => (
          <span key={i} style={{ color: part.color }}>
            {part.text}
          </span>
        ))}
      </motion.p>

      {/* The differentiator, in a single colour — it supports the statement
          above rather than competing with it. */}
      <p
        className="text-[17px] md:text-[21px] font-medium leading-[1.5] text-center max-w-[760px] -mt-6"
        style={{ color: BRAND.muted }}
      >
        {USP_TEXT}
      </p>

      <div className="w-full max-w-[1100px]">
        <ContactCTA message="Let's work together" buttonText="Contact us" scrollToId="contact" />
      </div>
    </section>
  );
}
